import React, { useEffect, useState } from 'react';
import { ArrowLeft, Search, User, Phone, MapPin, Calendar, Clock, Users, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import axiosInstance from '../api/axiosInstance';
import InactivityTimer from '../components/InactivityTimer';

const OnlineDeposit = () => {
    const navigate = useNavigate();
    const [mrno, setMrno] = useState('');
    const [patientData, setPatientData] = useState(null);
    const [patientList, setPatientList] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [collectionType, setCollectionType] = useState('On Account');
    const [depositAmount, setDepositAmount] = useState('');
    const [showPaymentStatusModal, setShowPaymentStatusModal] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentMessage, setPaymentMessage] = useState('');
    const [showNonIPStatusDialog, setShowNonIPStatusDialog] = useState(false);
    const [isNonIPPatient, setIsNonIPPatient] = useState(false);
    const [visitDetails, setVisitDetails] = useState(null);

    const serviceTypes = {
        // bed: 117,
        lab: 115,
        imaging: 116,
        ot: 1012
    };

    const fetchServices = async (type, serviceTypeId) => {
        // Deprecated - no longer needed
    };

    const fetchSearchResults = async (term) => {
        // Deprecated - no longer needed
    };

    // Fetch patient visit details to check IP status
    const fetchPatientVisitDetails = async (mrno) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(mrno)}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                },
            });

            if (response.data && response.data.status === 'success' && response.data.data && response.data.data[0]) {
                const visitData = response.data.data[0].visitDetails;
                setVisitDetails(visitData);

                // Check if patient status does NOT start with 'IP' OR visitStatus is not 'open' - only IP patients with open visit can make deposits
                if (visitData && visitData.patVisitStatus && (!visitData.patVisitStatus.startsWith('IP') || visitData.visitStatus !== 'open')) {
                    setIsNonIPPatient(true);
                    setShowNonIPStatusDialog(true);
                } else {
                    setIsNonIPPatient(false);
                }
            }
        } catch (err) {
            console.error('Error fetching visit details:', err);
        }
    };

    useEffect(() => {
        // Initialize app - no services to fetch anymore
    }, []);

    const handleInputChange = (e) => {
        setMrno(e.target.value);
        setError('');
    };

    const handleSearchChange = (e) => {
        // Deprecated
    };

    const handleFetchDetails = async () => {
        if (!mrno) {
            setError('Please enter a valid MRN or mobile number');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(mrno)}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1') || ''}` }
            });
            if (response.data.status === 'success' && Array.isArray(response.data.data) && response.data.data.length > 0) {
                if (response.data.data.length === 1) {
                    const patient = response.data.data[0];
                    setPatientData({
                        prefix: patient.prefix || '',
                        firstName: patient.firstName || '',
                        middleName: patient.middleName || '',
                        lastName: patient.lastName || '',
                        mrno: patient.mrno || '',
                        genderCode: patient.genderCode || '',
                        dob: patient.dobStr ? patient.dobStr.split('-').reverse().join('-') : '',
                        age: patient.age || '',
                        ageSelect: 'year',
                        contactNumber: patient.contactNo || '',
                        emailAddress: patient.email || '',
                        emergencyContactName: patient.emergencyContactName || '',
                        emergencyContactNo: patient.emergencyContactNo || patient.contactNo || '',
                        city: patient.addressList?.[0]?.cityName || '',
                        district: patient.addressList?.[0]?.districtName || '',
                        state: patient.addressList?.[0]?.stateName || '',
                        nationality: patient.addressList?.[0]?.countryName || '',
                        addressList: patient.addressList || [],
                        base64String: patient.photo || null
                    });
                    setPatientList([]);

                    // Fetch visit details to check IP status
                    await fetchPatientVisitDetails(patient.mrno);
                } else {
                    setPatientList(response.data.data);
                    setPatientData(null);
                }
                setError('');
            } else {
                setError('No patient found for this MRN or mobile number');
                setPatientData(null);
                setPatientList([]);
            }
        } catch (err) {
            setError('Failed to fetch patient details');
            setPatientData(null);
            setPatientList([]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleFetchDetails();
        }
    };

    const handleTabChange = (tab) => {
        // Deprecated
    };

    const addToCart = (service) => {
        // Deprecated
    };

    useEffect(() => {
        // Cart total calculation - deprecated
    }, []);

    const removeFromCart = (serviceId) => {
        // Deprecated
    };

    const updateQuantity = (serviceId, delta) => {
        // Deprecated
    };

    const handlePayment = async () => {
        // Prevent payment if patient is NOT IP
        if (isNonIPPatient) {
            setShowNonIPStatusDialog(true);
            return;
        }

        if (!depositAmount || parseFloat(depositAmount) <= 0) {
            setPaymentStatus('failure');
            setPaymentMessage('Please enter a valid deposit amount.');
            setShowPaymentStatusModal(true);
            return;
        }

        setLoading(true);
        try {
            // Create collection request
            const response = await axiosInstance.post('/api/patient/create-collection-order', {
                patientMrno: patientData.mrno,
                patientName: `${patientData.prefix || ''} ${patientData.firstName} ${patientData.middleName || ''} ${patientData.lastName}`.trim(),
                patientMobile: patientData.contactNumber,
                collectionType: collectionType,
                amount: parseFloat(depositAmount),
                kioskMachineNo: localStorage.getItem('kiosk_machine_no') || null,
                siteCode: localStorage.getItem('site_code') || null,
            }, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('kiosk_token')}` }
            });

            const { key, amount, currency, order_id } = response.data;

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = 'https://api.razorpay.com/v1/checkout/embedded';

            const formFields = {
                key_id: key,
                amount: amount.toString(),
                currency,
                order_id,
                name: "KIMS",
                description: "Online Deposit - " + collectionType,
                image: "/logo.jpg",
                callback_url: `${axiosInstance.defaults.baseURL}/api/patient/verify-collection-payment`,
                cancel_url: `${axiosInstance.defaults.baseURL}/api/patient/cancel-payment`,
                prefill: {
                    name: `${patientData.firstName || ''}  ${patientData.middleName || ''} ${patientData.lastName || ''}`.trim() || 'Test User',
                    email: patientData.emailAddress || 'test@gmail.com',
                    contact: patientData.contactNumber || '9427388748',
                },
                theme: { color: "#0FA968" },
                notes: {
                    collection_type: collectionType,
                    patient_mrno: patientData.mrno,
                },
            };

            Object.keys(formFields).forEach((key) => {
                if (typeof formFields[key] === 'object') {
                    Object.keys(formFields[key]).forEach((subKey) => {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = `${key}[${subKey}]`;
                        input.value = formFields[key][subKey];
                        form.appendChild(input);
                    });
                } else {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = formFields[key];
                    form.appendChild(input);
                }
            });

            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
        } catch (err) {
            setPaymentStatus('failure');
            setPaymentMessage('Error creating collection request: ' + (err.response?.data?.error || err.message || 'Unknown error'));
            setShowPaymentStatusModal(true);
            console.error('Error creating order:', err);
        } finally {
            setLoading(false);
        }
    };

    const allServices = [];

    const displayedServices = [];

    const renderPaymentStatusModal = () => {
        if (!paymentStatus || paymentStatus === 'success') return null;
        const statusConfig = {
            failure: { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "❌", title: "Payment Failed" },
            cancel: { bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-800", icon: "⚠️", title: "Payment Cancelled" }
        };
        const config = statusConfig[paymentStatus];
        return (
            <div className="fixed inset-0 z-50 flex justify-center items-center w-full h-full bg-black bg-opacity-50 overflow-y-auto">
                <div className="relative p-6 w-full max-w-lg bg-white rounded-2xl shadow-2xl">
                    <button
                        onClick={() => setShowPaymentStatusModal(false)}
                        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div className={`p-6 border-2 rounded-xl ${config.bg} ${config.border} text-center`}>
                        <span className="text-4xl mb-4 block">{config.icon}</span>
                        <h3 className={`font-bold text-xl ${config.text}`}>{config.title}</h3>
                        <p className={`mt-2 ${config.text} opacity-90`}>{paymentMessage}</p>
                        <div className="mt-6 flex justify-center gap-4">
                            <button
                                onClick={() => {
                                    setShowPaymentStatusModal(false);
                                    setPaymentStatus(null);
                                    setPaymentMessage("");
                                }}
                                className="px-6 py-2 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => setShowPaymentStatusModal(false)}
                                className="px-6 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg shadow hover:bg-gray-300 transition duration-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <InactivityTimer />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
                <div className="bg-white shadow-sm border-b">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between py-4">
                            <div className="flex items-center space-x-4">
                                <Link to="/" className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors duration-200 shadow-sm">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Back
                                </Link>
                                <div className="hidden sm:block w-px h-8 bg-gray-300"></div>
                                <h1 className="text-xl font-semibold text-gray-900">Advance Payment</h1>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {!patientData && patientList.length === 0 && (
                        <div className="flex justify-center items-center min-h-screen">
                            <div className="bg-white rounded-xl shadow-sm border p-6 mb-8 w-full max-w7-md">
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1">
                                        <label htmlFor="mrno" className="block text-sm font-medium text-gray-700 mb-2">
                                            MRN / Mobile Number
                                        </label>
                                        <input
                                            type="text"
                                            id="mrno"
                                            value={mrno}
                                            onChange={handleInputChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Enter MRN or mobile number to search patient details"
                                            className={`w-full px-4 py-3 border-2 rounded-lg transition-colors duration-200 ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-emerald-500 focus:ring-emerald-200'
                                                } focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                            aria-invalid={error ? 'true' : 'false'}
                                            aria-describedby="mrno-error"
                                        />
                                    </div>
                                    <div className="flex items-end">
                                        <button
                                            onClick={handleFetchDetails}
                                            disabled={loading}
                                            className={`px-8 py-3 bg-emerald-600 text-white font-medium rounded-lg shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-opacity-50 transition-all duration-200 ${loading ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                        >
                                            {loading ? (
                                                <div className="flex items-center">
                                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                    Searching...
                                                </div>
                                            ) : (
                                                <div className="flex items-center">
                                                    <Search className="w-4 h-4 mr-2" />
                                                    Get Details
                                                </div>
                                            )}
                                        </button>
                                    </div>
                                </div>
                                {error && (
                                    <div className="mt-4 flex items-center p-4 bg-red-50 border border-red-200 rounded-lg">
                                        <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                                        <p className="text-red-700 text-sm">{error}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {patientList.length > 1 && (
                        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-gray-800">Select Patient Profile</h2>
                                <button
                                    onClick={() => { setPatientList([]); setMrno(''); setError(''); }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    Back
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {patientList.map((patient, idx) => (
                                    <div key={patient.mrno + idx} className="border rounded-lg p-4 flex items-center gap-4 hover:shadow-lg transition cursor-pointer" onClick={() => {
                                        setPatientData({
                                            prefix: patient.prefix || '',
                                            firstName: patient.firstName || '',
                                            middleName: patient.middleName || '',
                                            lastName: patient.lastName || '',
                                            mrno: patient.mrno || '',
                                            genderCode: patient.genderCode || '',
                                            dob: patient.dobStr ? patient.dobStr.split('-').reverse().join('-') : '',
                                            age: patient.age || '',
                                            ageSelect: 'year',
                                            contactNumber: patient.contactNo || '',
                                            emailAddress: patient.email || '',
                                            emergencyContactName: patient.emergencyContactName || '',
                                            emergencyContactNo: patient.emergencyContactNo || patient.contactNo || '',
                                            city: patient.addressList?.[0]?.cityName || '',
                                            district: patient.addressList?.[0]?.districtName || '',
                                            state: patient.addressList?.[0]?.stateName || '',
                                            nationality: patient.addressList?.[0]?.countryName || '',
                                            addressList: patient.addressList || [],
                                            base64String: patient.photo || null
                                        });
                                        setPatientList([]);

                                        // Fetch visit details to check IP status
                                        fetchPatientVisitDetails(patient.mrno);
                                    }}>
                                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center">
                                            {patient.photo ? (
                                                <img src={patient.photo} alt="Patient" className="w-full h-full object-cover rounded-xl" />
                                            ) : (
                                                <User className="w-8 h-8 text-emerald-600" />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="text-base font-semibold text-gray-900">
                                                {`${patient.prefix || ''} ${patient.firstName} ${patient.middleName || ''} ${patient.lastName}`.trim()}
                                            </h3>
                                            <p className="text-xs text-gray-500">MRNO: {patient.mrno}</p>
                                            <p className="text-xs text-gray-500">Mobile: {patient.contactNo}</p>
                                            <p className="text-xs text-gray-500">DOB: {patient.dobStr ? patient.dobStr.split('-').reverse().join('-') : ''}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {patientData && (
                        <div className="flex flex-col lg:flex-row gap-6">
                            <div className="lg:w-[70%]">
                                <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-6">
                                    <div className="bg-emerald-50 border-b border-emerald-100 px-6 py-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <CheckCircle className="w-5 h-5 text-emerald-600 mr-3" />
                                                <p className="text-emerald-800 font-medium">Patient Details Retrieved Successfully</p>
                                            </div>
                                            <button
                                                onClick={() => { setPatientData(null); setError(''); setMrno(''); }}
                                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                            >
                                                Re-enter MRN / Mobile Number
                                            </button>
                                        </div>
                                    </div>
                                    <div className="p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="flex items-center space-x-4">
                                                <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center">
                                                    {patientData.base64String ? (
                                                        <img
                                                            src={patientData.base64String}
                                                            alt="Patient"
                                                            className="w-full h-full object-cover rounded-xl"
                                                        />
                                                    ) : (
                                                        <User className="w-8 h-8 text-emerald-600" />
                                                    )}
                                                </div>
                                                <div>
                                                    <h3 className="text-base font-semibold text-gray-900">
                                                        {`${patientData.prefix || ''} ${patientData.firstName} ${patientData.middleName || ''} ${patientData.lastName}`.trim()}
                                                    </h3>
                                                    <p className="text-xs text-gray-500">MRNO: {patientData.mrno}</p>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex items-center">
                                                    <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                                                    <div>
                                                        <p className="text-xs text-gray-500">DOB / Age</p>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {patientData.dob} ({patientData.age} {patientData.ageSelect || 'year'}(s))
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                                                    <div>
                                                        <p className="text-xs text-gray-500">Mobile</p>
                                                        <p className="text-sm font-medium text-gray-900">{patientData.contactNumber}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex items-center">
                                                    <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                                                    <div>
                                                        <p className="text-xs text-gray-500">Address</p>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {`${patientData.addressList?.[0]?.address || ''}, ${patientData.city}, ${patientData.state}`.trim()}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <Users className="w-4 h-4 text-gray-400 mr-2" />
                                                    <div>
                                                        <p className="text-xs text-gray-500">Emergency Contact</p>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {patientData.emergencyContactName} ({patientData.emergencyContactNo})
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl shadow-sm border p-6">
                                    <div className="mb-6">
                                        <h2 className="text-lg font-semibold text-gray-800 mb-4">Add Collection Request</h2>
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label htmlFor="collectionType" className="block text-sm font-medium text-gray-700 mb-2">
                                                        Collection Type
                                                    </label>
                                                    <select
                                                        id="collectionType"
                                                        value={collectionType}
                                                        disabled
                                                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                                                    >
                                                        <option value="On Account">On Account</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label htmlFor="depositAmount" className="block text-sm font-medium text-gray-700 mb-2">
                                                        Amount
                                                    </label>
                                                    <input
                                                        type="number"
                                                        id="depositAmount"
                                                        value={depositAmount}
                                                        onChange={(e) => setDepositAmount(e.target.value)}
                                                        placeholder="Enter amount"
                                                        min="0"
                                                        step="0.01"
                                                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="lg:w-[30%]">
                                <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-6">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Collection Summary</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Collection Type:</span>
                                                <span className="font-semibold text-gray-900">{collectionType}</span>
                                            </div>
                                            <div className="flex justify-between border-t pt-3">
                                                <span className="text-gray-600">Amount:</span>
                                                <span className="text-lg font-bold text-emerald-600">
                                                    ₹{depositAmount ? parseFloat(depositAmount).toFixed(2) : '0.00'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <button
                                            onClick={handlePayment}
                                            className={`w-full py-3 bg-emerald-600 text-white font-semibold rounded-lg shadow hover:bg-emerald-700 transition duration-200 ${!depositAmount || parseFloat(depositAmount) <= 0 || loading ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                            disabled={!depositAmount || parseFloat(depositAmount) <= 0 || loading}
                                        >
                                            {loading ? (
                                                <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                                            ) : (
                                                'Proceed'
                                            )}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setPatientData(null);
                                                setDepositAmount('');
                                                setCollectionType('On Account');
                                                setMrno('');
                                            }}
                                            className="w-full py-3 bg-gray-300 text-gray-800 font-semibold rounded-lg shadow hover:bg-gray-400 transition duration-200"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {renderPaymentStatusModal()}
                </div>
            </div>

            {/* Non-IP Status Dialog */}
            {showNonIPStatusDialog && (
                <div className="fixed inset-0 z-50 flex justify-center items-center w-full h-full bg-black bg-opacity-50">
                    <div className="relative p-8 w-full max-w-md bg-white rounded-2xl shadow-2xl">
                        <div className="text-center">
                            <div className="w-20 h-20 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                                <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-[#22292A] mb-4">
                                Cannot Process Deposit
                            </h3>
                            <p className="text-gray-600 text-lg leading-relaxed mb-4">
                                {!visitDetails?.patVisitStatus?.startsWith('IP') ? (
                                    <>Deposits can only be made for In-Patient (IP) visits. Your current visit status is <strong className="text-red-600">{visitDetails?.patVisitStatus}</strong>.</>
                                ) : (
                                    <>Deposits can only be made for visits with <strong>open</strong> status. Your current visit status is <strong className="text-red-600">{visitDetails?.visitStatus}</strong>.</>
                                )}
                            </p>
                            <div className="bg-red-50 rounded-lg p-4 mb-6">
                                <p className="text-sm text-gray-700">
                                    <strong>Patient Visit Status:</strong> {visitDetails?.patVisitStatus}<br />
                                    <strong>Visit Status:</strong> {visitDetails?.visitStatus}<br />
                                    <strong>Last Visit:</strong> {visitDetails?.lastVisitNo}<br />
                                    <strong>Visit Date:</strong> {visitDetails?.visitDate}
                                </p>
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => navigate('/')}
                                    className="flex-1 bg-[#0FA968] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#138F5E] transition duration-200"
                                >
                                    Go to Home
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OnlineDeposit;